/**
 * API Service - Helper for making authenticated backend API calls with Clerk JWT tokens
 *
 * IMPORTANT: Supabase Edge Functions have built-in JWT auth that intercepts the Authorization header.
 * To use Clerk JWTs instead, we:
 * 1. Send Supabase anon key in Authorization header (to pass Supabase's gateway)
 * 2. Send Clerk JWT in x-clerk-token header (for our custom middleware to verify)
 */

import { publicAnonKey } from '../../../utils/supabase/info';
import { logger } from '../utils/logger';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string;

export interface RunRecord {
  id: string;
  jobUrl?: string;
  jobTitle?: string;
  company?: string;
  jobRole?: string;
  status?: string;
  createdAt?: string;
  prospectsFound?: number;
  campaignStatus?: string;
  campaignId?: string;
  error?: string;
  prospects?: ProspectRecord[];
  [key: string]: unknown;
}

export interface ProspectRecord {
  id?: string;
  run_id?: string;
  name?: string;
  title?: string;
  company?: string;
  linkedin_url?: string;
  stage?: string;
  relevanceScore?: number;
  aiMatchScore?: number;
  matchingReason?: string;
  messages?: Record<string, unknown>[];
  userAvatar?: string;
  [key: string]: unknown;
}

export interface PipelineResponse {
  prospects?: ProspectRecord[];
  stageCounts?: Record<string, number>;
  [key: string]: unknown;
}

export interface RunDetailResponse {
  run?: RunRecord;
  prospects?: ProspectRecord[];
  /** Messages fetched directly by run_id — covers messages with no prospect linkage */
  messages?: Record<string, unknown>[];
}

export interface AnalyticsResponse {
  [key: string]: unknown;
}

if (!API_BASE_URL) {
  logger.error('[API] Missing VITE_API_BASE_URL environment variable. Set it in your .env file or Vercel dashboard.');
}

const REQUEST_TIMEOUT_MS = 30_000; // 30 seconds
const MAX_RETRIES = 1;
const RETRY_DELAY_MS = 1_000;

/**
 * Make an authenticated API request to the backend with timeout and retry on 5xx.
 */
export async function apiRequest<T = unknown>(
  endpoint: string,
  options: RequestInit,
  getToken: () => Promise<string | null>
): Promise<T> {
  const clerkToken = await getToken();

  if (!clerkToken) {
    throw new Error('Authentication required - no valid token available');
  }

  const url = `${API_BASE_URL}${endpoint}`;

  const attempt = async (): Promise<Response> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      return await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: {
          'Authorization': `Bearer ${publicAnonKey}`,
          'x-clerk-token': clerkToken,
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });
    } finally {
      clearTimeout(timeoutId);
    }
  };

  let lastError: Error | null = null;

  for (let i = 0; i <= MAX_RETRIES; i++) {
    if (i > 0) {
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS * i));
      logger.info(`Retrying request to ${endpoint} (attempt ${i + 1})`);
    }

    let response: Response;
    try {
      response = await attempt();
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') {
        throw new Error(`Request timed out after ${REQUEST_TIMEOUT_MS / 1000}s: ${endpoint}`);
      }
      lastError = err instanceof Error ? err : new Error(String(err));
      // Only retry on network errors, not on abort
      continue;
    }

    // Retry on 5xx server errors only
    if (response.status >= 500 && i < MAX_RETRIES) {
      lastError = new Error(`Server error ${response.status} on ${endpoint}`);
      continue;
    }

    if (!response.ok) {
      const errorText = await response.text();
      logger.error(`API error on ${endpoint}:`, errorText);
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }

    return null as T;
  }

  throw lastError ?? new Error(`Request failed after ${MAX_RETRIES + 1} attempts: ${endpoint}`);
}

/** GET request helper */
export async function apiGet<T = unknown>(
  endpoint: string,
  getToken: () => Promise<string | null>
): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'GET' }, getToken);
}

/** POST request helper */
export async function apiPost<T = unknown>(
  endpoint: string,
  body: unknown,
  getToken: () => Promise<string | null>
): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'POST', body: JSON.stringify(body) }, getToken);
}

/** PUT request helper */
export async function apiPut<T = unknown>(
  endpoint: string,
  body: unknown,
  getToken: () => Promise<string | null>
): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'PUT', body: JSON.stringify(body) }, getToken);
}

/** PATCH request helper */
export async function apiPatch<T = unknown>(
  endpoint: string,
  body: unknown,
  getToken: () => Promise<string | null>
): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'PATCH', body: JSON.stringify(body) }, getToken);
}

/** DELETE request helper */
export async function apiDelete<T = unknown>(
  endpoint: string,
  getToken: () => Promise<string | null>
): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'DELETE' }, getToken);
}

// ============================================================================
// Domain-Specific API Functions
// ============================================================================

/**
 * Fetches runs for the current organization with pagination.
 */
export async function getRuns(
  getToken: () => Promise<string | null>,
  page: number = 1,
  limit: number = 20
): Promise<{ runs: RunRecord[], totalCount: number }> {
  const response = await apiGet<{ runs: RunRecord[] }>('/runs', getToken);
  const allRuns = response.runs || [];

  const startIndex = (page - 1) * limit;
  const paginatedRuns = allRuns.slice(startIndex, startIndex + limit);

  return { runs: paginatedRuns, totalCount: allRuns.length };
}

/** Creates a new run */
export async function createRun(
  data: { jobUrl: string; jobTitle: string; company: string; notes?: string },
  getToken: () => Promise<string | null>
): Promise<RunRecord> {
  return apiPost<RunRecord>('/runs/create', data, getToken);
}

/** Fetches a single run by ID */
export async function getRun(
  runId: string,
  getToken: () => Promise<string | null>
): Promise<RunDetailResponse> {
  return apiGet<RunDetailResponse>(`/runs/${runId}`, getToken);
}

/** Fetches all prospects via the pipeline endpoint */
export async function getProspects(
  getToken: () => Promise<string | null>
): Promise<ProspectRecord[]> {
  const response = await getPipeline(getToken);
  return response.prospects || [];
}

/** Fetches all messages */
export async function getMessages(
  getToken: () => Promise<string | null>
): Promise<unknown[]> {
  const response = await apiGet<{ messages: Record<string, unknown>[] }>('/messages', getToken);
  return response.messages || [];
}

/** Fetches pipeline data */
export async function getPipeline(
  getToken: () => Promise<string | null>
): Promise<PipelineResponse> {
  return apiGet<PipelineResponse>('/pipeline', getToken);
}

/** Fetches analytics data */
export async function getAnalytics(
  getToken: () => Promise<string | null>
): Promise<AnalyticsResponse> {
  return apiGet<AnalyticsResponse>('/analytics', getToken);
}

/** Deletes a run and all associated data */
export async function deleteRun(
  runId: string,
  getToken: () => Promise<string | null>
): Promise<void> {
  await apiDelete<void>(`/runs/${runId}`, getToken);
}

/** Updates a manually-added job's status. Not supported for automation-run-derived jobs. */
export async function updateJobStatus(
  userId: string,
  jobId: string,
  status: string,
  getToken: () => Promise<string | null>
): Promise<{ success: boolean }> {
  return apiPatch<{ success: boolean }>(`/user-data/${userId}/jobs/${jobId}`, { status }, getToken);
}

/** Fetches prospects for a specific run */
export async function getProspectsByRun(
  runId: string,
  getToken: () => Promise<string | null>
): Promise<ProspectRecord[]> {
  const response = await getRun(runId, getToken);
  return response.prospects || [];
}

/** Updates a prospect's pipeline stage */
export async function updateProspectStage(
  prospectId: string,
  data: { stage: string; notes?: string },
  getToken: () => Promise<string | null>
): Promise<Record<string, unknown>> {
  return apiPut<Record<string, unknown>>(`/prospects/${prospectId}/stage`, data, getToken);
}

/** Fetches messages for a specific prospect */
export async function getMessagesByProspect(
  prospectId: string,
  getToken: () => Promise<string | null>
): Promise<Record<string, unknown> | null> {
  const pipeline = await getPipeline(getToken);
  const prospect = pipeline.prospects?.find((p) => p.id === prospectId);
  return (prospect?.message as Record<string, unknown>) || null;
}

/** Sends a message to a prospect */
export async function sendMessage(
  prospectId: string,
  data: {
    connectionRequest?: string;
    followUp1?: string;
    followUp2?: string;
    followUp3?: string;
  },
  getToken: () => Promise<string | null>
): Promise<Record<string, unknown>> {
  return apiPost<Record<string, unknown>>(`/messages/${prospectId}`, data, getToken);
}
